import React from "react";
import ApiIcon from '@mui/icons-material/Api';

import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import HomeIcon from '@mui/icons-material/Home';
import SettingsIcon from '@mui/icons-material/Settings';

import Pricing from "components/domain/pricing/Pricing.js";
import useSeo from "utils/useSeo";
import Index from "components/domain/Index.js";

import BuildIcon from '@mui/icons-material/Build';
import ArticleIcon from '@mui/icons-material/Article';
import RocketIcon from '@mui/icons-material/Rocket';
import SellIcon from '@mui/icons-material/Sell';
import WorkIcon from '@mui/icons-material/Work';
import SecurityIcon from '@mui/icons-material/Security';
import PermMedia from "@mui/icons-material/PermMedia";
import Careers from "components/domain/careers/Careers.js";
import UseCasesPage from "components/domain/use-cases/UseCasesPage.js";
import IndustriesHub from "components/domain/industries/IndustriesHub.jsx";
import IndustryLandingPage from "components/domain/industries/IndustryLandingPage.jsx";
import DomainIcon from "@mui/icons-material/Domain";
import { API_LANDING_ENABLED } from "utils/apiLanding";
import { pageDescription } from "utils/routeMeta";

/*
The optimizer and the model builder are loaded on demand.

They were plain imports, and because routes.js is pulled in by BOTH layouts —
for the sidebar as well as for rendering — everything they reach ended up in the
entry bundle. Measured against production on 2026-09-04, main.js was 6.0 MB
uncompressed / 1.77 MB over the wire, and it loaded on every route: somebody
reading the /home headline downloaded mapbox-gl, deck.gl, ag-grid, antd, xlsx
and recharts first. Ad visitors average 1.18 page views before leaving, and no
amount of ad copy fixes a multi-second first paint.

Marketing pages stay eager on purpose. They are the entry experience, they are
small, and a Suspense flash on the page an ad click lands on would trade one
problem for another.
*/
const DataDashboard = React.lazy(() => import("components/domain/models/DataDashboard.js"));
const Wizard = React.lazy(() => import("components/domain/wizard").then((m) => ({ default: m.Wizard })));
const ExampleScenarioRoute = React.lazy(() => import("components/domain/examples/ExampleScenarioRoute.js"));
const AccountDashboard = React.lazy(() => import("components/domain/account/AccountDashboard.js"));
/* Lazy like its neighbours: while the flag is off nothing renders it, and even
   once it is on this is a marketing page that most visitors never open. */
const ApiLanding = React.lazy(() => import("components/domain/api/ApiLanding.js"));



const TermsOfService = () => {
  useSeo({ title: "Terms of Service", description: pageDescription("/terms-of-service"), canonicalPath: "/terms-of-service" });
  const containerRef = React.useRef(null);
  
  React.useEffect(() => {
    const loadTermly = () => {
      // Remove existing script to force reload
      const existingScript = document.getElementById('termly-embed-script');
      if (existingScript) {
        existingScript.remove();
      }
      
      // Load fresh script
      const js = document.createElement('script');
      js.id = 'termly-embed-script';
      js.src = "https://app.termly.io/embed-policy.min.js";
      js.onload = () => {
        // Give script time to initialize
        setTimeout(() => {
          if (window.displayPreferences) {
            window.displayPreferences();
          }
        }, 100);
      };
      document.body.appendChild(js);
    };
    
    loadTermly();
    
    return () => {
      // Cleanup on unmount
      const script = document.getElementById('termly-embed-script');
      if (script) {
        script.remove();
      }
    };
  }, []);
  
  return (
    <div ref={containerRef} name="termly-embed" data-id="9b205f9e-5997-4d94-b322-0be645ad8609" data-type="iframe"></div>
  );
};

const PrivacyPolicy = () => {
  useSeo({ title: "Privacy Policy", description: pageDescription("/privacy-policy"), canonicalPath: "/privacy-policy" });
  const containerRef = React.useRef(null);
  
  React.useEffect(() => {
    const loadTermly = () => {
      // Remove existing script to force reload
      const existingScript = document.getElementById('termly-embed-script');
      if (existingScript) {
        existingScript.remove();
      }
      
      // Load fresh script
      const js = document.createElement('script');
      js.id = 'termly-embed-script';
      js.src = "https://app.termly.io/embed-policy.min.js";
      js.onload = () => {
        // Give script time to initialize
        setTimeout(() => {
          if (window.displayPreferences) {
            window.displayPreferences();
          }
        }, 100);
      };
      document.body.appendChild(js);
    };
    
    loadTermly();
    
    return () => {
      // Cleanup on unmount
      const script = document.getElementById('termly-embed-script');
      if (script) {
        script.remove();
      }
    };
  }, []);
  
  return (
    <div ref={containerRef} name="termly-embed" data-id="00a43398-18f8-4a0c-b226-c629e67703dd" data-type="iframe"></div>
  );
};


class PickupDropoffDashboard extends React.Component {
  render() {
    return <DataDashboard modelType={'pickup-delivery'}></DataDashboard>;
  }
}

class VehicleRoutingDashboard extends React.Component {
  render() {
    return <DataDashboard modelType={'vehicle-routing'}></DataDashboard>;
  }
}


const routes = [
  {
    path: "",
    name: "Home",
    icon: HomeIcon,
    iconColor: "Default",
    component: Index,
    layout: "/home",
    hide: true,
  },
  {
    path: "/",
    name: "Model Builder",
    icon: BuildIcon,
    iconColor: "Default",
    component: Wizard,
    layout: "/model-builder",
  },
  {
    collapse: true,
    name: "Optimizers",
    icon: RocketIcon,
    iconColor: "Primary",
    state: "dashboardsCollapse",
    views: [
      {
        path: "/vehicle-routing",
        name: "Vehicle Routing",
        miniName: "VR",
        component: VehicleRoutingDashboard,
        layout: "/models",
      },
      {
        path: "/pickup-dropoff",
        name: "Pickup & Dropoff Routing",
        miniName: "PD",
        component: PickupDropoffDashboard,
        layout: "/models",
      },
    ],
  },
  {
    // Single entry into the example gallery. This was a collapse that injected
    // one sub-item per published example, which stopped scaling once the
    // catalog passed a few dozen — a flat list of names can't convey the
    // banner, description, or constraints a card does, and it grew without
    // bound. The gallery is the place to browse; the sidebar just points at it.
    collapse: false,
    path: "/",
    name: "Examples",
    icon: PermMedia,
    iconColor: "Warning",
    component: UseCasesPage,
    layout: "/use-cases",
  },
  {
    // Registers the dynamic /examples/:slug route in the Switch. Hidden from
    // the sidebar: individual examples are reached by clicking a card.
    path: '/:slug',
    name: 'Example',
    component: ExampleScenarioRoute,
    layout: '/examples',
    hide: true,
  },
  {
    // Registers /industries/:slug in the Switch. Must precede the hub entry
    // below so a slug URL matches the detail page, not the (prefix) hub route.
    // Hidden from the sidebar — reached from the Navbar / Footer / hub cards.
    path: "/:slug",
    name: "Industry",
    component: IndustryLandingPage,
    layout: "/industries",
    hide: true,
  },
  {
    collapse: false,
    path: "/",
    name: "Industries",
    icon: DomainIcon,
    iconColor: "Primary",
    component: IndustriesHub,
    layout: "/industries",
    hide: true,
  },
  {
    // Registers /industries/:slug in the Switch. Must precede the hub entry
    // below so a slug URL matches the detail page, not the (prefix) hub route.
    // Hidden from the sidebar — reached from the Navbar / Footer / hub cards.
    path: "/:slug",
    name: "Industry",
    component: IndustryLandingPage,
    layout: "/industries",
    hide: true,
  },
  {
    collapse: false,
    path: "/",
    name: "Industries",
    icon: DomainIcon,
    iconColor: "Primary",
    component: IndustriesHub,
    layout: "/industries",
    hide: true,
  },
  {
    collapse: false,
    path: "/",
    name: "Pricing",
    icon: SellIcon,
    iconColor: "Success",
    component: Pricing,
    layout: "/pricing",
  },
  {
    divider: true,
  },
  {
    title: "Documentation",
  },
  {
    /* Off: a redirect straight out to the docs portal, which is what this
       sidebar item, the Navbar and the Footer have always done. On: our own
       landing page, which links to the portal rather than jumping to it — the
       site's only surface for "route optimization API". One flag, because the
       route, the page metadata and the sitemap all have to move together.
       See src/utils/apiLanding.js. */
    path:"/",
    name: "API",
    icon: ApiIcon,
    component: API_LANDING_ENABLED
      ? ApiLanding
      : () => { window.location.replace(process.env.REACT_APP_DOCS_URL); return null; },
    layout: "/api"
  },
  {
    path:"/",
    name: "Account",
    icon: AccountCircleIcon,
    component: AccountDashboard,
    layout: "/account"
  },
  {
    path: "/",
    name: "Settings",
    icon: SettingsIcon,
    layout: "/admin",
    isDialog: true,
  },
  {
    path:"/",
    name: "Terms of Service",
    icon: ArticleIcon,
    component: TermsOfService,
    layout: "/terms-of-service"
  },
  {
    path:"/",
    name: "Privacy Policy",
    icon: SecurityIcon,
    component: PrivacyPolicy,
    layout: "/privacy-policy"
  },
  {
    path: "/",
    name: "Careers",
    icon: WorkIcon,
    component: Careers,
    layout: "/careers"
  },
];
export default routes;
